import { Node } from "@tiptap/pm/model";
import { EditorState } from "@tiptap/pm/state";

import { ITranslationData } from "@/view/TiptapArticleBody/components/TranslateDrawer/TranslateDrawer.interface";
import { getDynamicData } from "@/view/TiptapArticleBody/extensions/DataProvider";

export interface ITranslationTrackingMetadata {
  translation_scope?: string | undefined;
  original_text_length?: number | undefined;
  translated_text_length?: number | undefined;
  user_type?: "teacher" | "student" | undefined;
  subscription_type?: "lite" | "premium" | undefined;
  article_level?: string | number | undefined;
  content_provider?: string | undefined;
  translation_session_id?: string | undefined;
  article_story_content_id?: string | undefined;
  translation_scope_index?: string | undefined;
  text_start_position?: number | undefined;
  text_end_position?: number | undefined;
  selected_language?: string | undefined;
  previous_language?: string | undefined;
  assignment_id?: string | undefined;
  content_type?: string | undefined;
  selection_context?: {
    is_first_selection: boolean;
    time_since_last_selection?: number;
  };
}

interface UseTranslationEventTrackingParams {
  state: EditorState;
  node?: Node;
  translationData?: ITranslationData;
  isFirstSelection?: boolean;
  timeSinceLastSelection?: number | undefined;
  selectedLanguage?: { code: string; name: string } | undefined;
  previousLanguage?: string | undefined;
}

export default function useTranslationEventTracking({
  state,
  node,
  translationData,
  isFirstSelection,
  timeSinceLastSelection,
  selectedLanguage,
  previousLanguage,
}: UseTranslationEventTrackingParams): Record<string, unknown> {
  const metadata: ITranslationTrackingMetadata = {};
  const { content, levelId, lexile, userProperties, assignment } =
    getDynamicData(state) || {};

  metadata.user_type = userProperties.isTeacher ? "teacher" : "student";
  metadata.subscription_type = userProperties.isLiteUser ? "lite" : "premium";
  metadata.article_level = lexile;
  metadata.article_story_content_id = levelId;
  metadata.content_provider =
    content?.data?.content_provider?.name || "Newsela";

  metadata.assignment_id =
    "assignment_id" in assignment && assignment.assignment_id
      ? assignment.assignment_id.toString()
      : undefined;
  metadata.content_type = content?.content_type;
  metadata.selected_language =
    selectedLanguage?.code || translationData?.target_language?.code;
  metadata.previous_language =
    previousLanguage || translationData?.source_language;

  // Use the isFirstSelection and timeSinceLastSelection parameters passed from the parent component
  metadata.selection_context = {
    is_first_selection: isFirstSelection ?? false,
    ...(timeSinceLastSelection !== undefined && {
      time_since_last_selection: timeSinceLastSelection,
    }),
  };

  if (node) {
    const nodeIdentifier = `${node?.attrs["data-identifier"] || ""}`;
    let start, end;

    state.doc.descendants((n, pos) => {
      if (`${n.attrs["data-identifier"] || ""}` === nodeIdentifier) {
        start = pos;
        end = pos + node.nodeSize;
        return false; // Stop traversing once we find the node
      } else return true; // Continue traversing
    });

    metadata.translation_scope = node.type.name;
    metadata.original_text_length = node.textContent.trim().length;
    metadata.translation_scope_index = nodeIdentifier.replace(/[^0-9]/g, "");
    metadata.text_start_position = start;
    metadata.text_end_position = end;
  }

  if (translationData) {
    metadata.translated_text_length = translationData.translated_text?.length;
    metadata.translation_session_id = translationData.identifier;
  }

  return metadata as Record<string, unknown>;
}
